import L from "leaflet";
import { getTileUrlRequest } from "../../api/plansApi";

/**
 * TileLayer personalizado que obtiene URLs firmadas (TTL 15 min) por cada tile
 * mediante GET /plans/:id/tile-url. Cachea la URL en memoria durante 12 min
 * para evitar llamadas redundantes al hacer pan/zoom.
 */
interface CachedUrl {
  url: string;
  expiresAtMs: number;
}

const TILE_CACHE_TTL_MS = 12 * 60 * 1000; // 12 minutos (URL del backend vive 15)

export interface SignedTileLayerOptions extends L.TileLayerOptions {
  planId: string;
}

export const SignedTileLayer = L.TileLayer.extend({
  initialize(options: SignedTileLayerOptions) {
    this._planId = options.planId;
    this._urlCache = new Map<string, CachedUrl>();
    (L.TileLayer.prototype as unknown as {
      initialize: (url: string, opts: L.TileLayerOptions) => void;
    }).initialize.call(this, "", options);
  },

  // Invalida la caché (p. ej. tras refresco de token) para re-pedir URLs firmadas.
  invalidateCache() {
    (this as unknown as { _urlCache: Map<string, CachedUrl> })._urlCache.clear();
  },

  createTile(coords: L.Coords, done: L.DoneCallback): HTMLImageElement {
    const img = document.createElement("img");
    img.alt = "";
    img.setAttribute("role", "presentation");

    const self = this as unknown as {
      _planId: string;
      _urlCache: Map<string, CachedUrl>;
    };

    const key = `${coords.z}/${coords.x}/${coords.y}`;
    const now = Date.now();
    const cached = self._urlCache.get(key);

    const loadFromUrl = (url: string) => {
      img.onload = () => done(undefined, img);
      img.onerror = (err) => done(err as unknown as Error, img);
      img.src = url;
    };

    if (cached && cached.expiresAtMs > now) {
      loadFromUrl(cached.url);
      return img;
    }

    getTileUrlRequest(self._planId, coords.z, coords.x, coords.y)
      .then((res) => {
        self._urlCache.set(key, {
          url: res.signedUrl,
          expiresAtMs: Date.now() + TILE_CACHE_TTL_MS,
        });
        loadFromUrl(res.signedUrl);
      })
      .catch((err) => {
        // 404 = tile inexistente (bordes del plano): silenciamos,
        // Leaflet solicita tiles fuera de bounds.
        done(err instanceof Error ? err : new Error(String(err)), img);
      });

    return img;
  },
}) as unknown as new (opts: SignedTileLayerOptions) => L.TileLayer & {
  invalidateCache: () => void;
};
